import type { DownloadReporter } from '@mimir/client-common/model/ongoingDownload';
import type { Logger } from '@mimir/common/types/logger';
import { exceptionToString } from '@mimir/common/util/exception';
import type { ExternalSmTransferMonitor } from './externalSmTransferMonitor';

interface TransferWaiter {
  promise: Promise<string>;
  resolve: (localPath: string) => void;
  reject: (err: unknown) => void;
  candidatePaths: string[];
  reporters: DownloadReporter[];
  /**
   * True while waitForFile() is in progress for this transfer.
   * Prevents concurrent poll cycles from starting a second finalization
   * for the same transfer.
   */
  finalizing: boolean;
}

/**
 * Implements ExternalSmTransferMonitor by maintaining a shared polling interval
 * over all pending transfers. Polling starts on the first waitForLocalFile() call
 * and stops automatically once all transfers have been resolved.
 *
 * setInterval and clearInterval are injected for testability.
 */
export class ExternalSmTransferMonitorImpl implements ExternalSmTransferMonitor {

  private timerId: number | undefined;
  private waiters: Map<string, TransferWaiter> = new Map();

  constructor(
    private logger: Logger,
    private pollDelayInMs: number,
    /**
     * Returns the IDs of transfers that are still active on the server.
     * A transfer is considered complete when its ID is absent from this list.
     */
    private getActiveTransferIds: () => Promise<string[]>,
    /**
     * Waits for the file to appear at one of the candidate paths and returns
     * the path where it was found. Throws if the file does not appear within
     * the allowed deadline.
     */
    private waitForFile: (candidatePaths: string[], reporters: DownloadReporter[]) => Promise<string>,
    private setIntervalFn: (callback: () => void, ms: number) => number,
    private clearIntervalFn: (handle: number) => void,
  ) {}

  isTracking(transferId: string): boolean {
    return this.waiters.has(transferId);
  }

  waitForLocalFile(
    transferId: string,
    candidatePaths: string[],
    reporters: DownloadReporter[],
  ): Promise<string> {
    const existing = this.waiters.get(transferId);
    if (existing !== undefined) {
      return existing.promise;
    }

    let resolveFn!: (localPath: string) => void;
    let rejectFn!: (err: unknown) => void;
    const promise = new Promise<string>((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });

    this.waiters.set(transferId, {
      promise,
      resolve: resolveFn,
      reject: rejectFn,
      candidatePaths,
      reporters,
      finalizing: false,
    });

    this.startPolling();
    return promise;
  }

  private startPolling() {
    if (this.timerId !== undefined) {
      return;
    }
    this.timerId = this.setIntervalFn(() => {
      void this.pollForProgress();
    }, this.pollDelayInMs);
  }

  private stopPolling() {
    if (this.timerId !== undefined) {
      this.clearIntervalFn(this.timerId);
      this.timerId = undefined;
    }
  }

  private async pollForProgress() {
    if (this.waiters.size === 0) {
      this.stopPolling();
      return;
    }

    let activeIds: Set<string>;
    try {
      activeIds = new Set(await this.getActiveTransferIds());
    } catch (err) {
      this.logger.error(`ExternalSmTransferMonitor: error fetching active transfers: ${exceptionToString(err)}`);
      return;
    }

    for (const [transferId, waiter] of this.waiters) {
      if (waiter.finalizing || activeIds.has(transferId)) {
        continue;
      }
      waiter.finalizing = true;
      void this.finalizeTransfer(transferId, waiter);
    }
  }

  private async finalizeTransfer(transferId: string, waiter: TransferWaiter) {
    try {
      const localPath = await this.waitForFile(waiter.candidatePaths, waiter.reporters);
      waiter.resolve(localPath);
    } catch (err) {
      waiter.reject(err);
    } finally {
      this.waiters.delete(transferId);
    }
  }

}
